export const FEATURE_KEYS = [
  "orders",
  "documents",
  "inventory",
  "accounts",
  "sampleOrders",
  "tasks",
  "grids",
  "team",
  "map",
  "adminSettings",
  "connections",
  "sampleOrderMetrics",
  "accountAnalytics",
  "inventoryMetrics",
];

export const FEATURE_LABELS = {
  orders: "Orders",
  documents: "Documents",
  inventory: "Inventory",
  accounts: "Accounts",
  sampleOrders: "Sample Orders",
  tasks: "Tasks",
  grids: "Grids",
  team: "Team",
  map: "Map",
  adminSettings: "Admin Settings",
  connections: "Connections",
  sampleOrderMetrics: "Sample Order Metrics",
  accountAnalytics: "Account Analytics",
  inventoryMetrics: "Inventory Metrics",
};

const normalizeEmail = (value) => (value || "").toString().trim().toLowerCase();

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const parseSeatCount = (value) => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
};

const hasPaidSeatCapacity = (seatLimits = {}) => {
  const adminSeats = parseSeatCount(seatLimits?.admin);
  const endUserSeats = parseSeatCount(seatLimits?.endUser);
  return adminSeats > 0 || endUserSeats > 0;
};

export const getFeatureFlagMap = (featureFlags = {}, bucketKey) => {
  const raw = featureFlags?.[bucketKey];
  return raw && typeof raw === "object" ? raw : {};
};

export const mergeFeatureFlags = (companyFlags = {}, globalFlags = {}) => ({
  ...(companyFlags || {}),
  ...(globalFlags || {}),
  hiddenEntirelyFeatures: {
    ...(companyFlags?.hiddenEntirelyFeatures || {}),
    ...(globalFlags?.hiddenEntirelyFeatures || {}),
  },
  hiddenFeatures: {
    ...(companyFlags?.hiddenFeatures || {}),
    ...(globalFlags?.hiddenFeatures || {}),
  },
  paidOnlyFeatures: {
    ...(companyFlags?.paidOnlyFeatures || {}),
    ...(globalFlags?.paidOnlyFeatures || {}),
  },
  featureAllowEmails: {
    ...(companyFlags?.featureAllowEmails || {}),
    ...(globalFlags?.featureAllowEmails || {}),
  },
  featureLockMessages: {
    ...(companyFlags?.featureLockMessages || {}),
    ...(globalFlags?.featureLockMessages || {}),
  },
});

export const isPaidCompanyState = ({
  planType,
  subscriptionActive,
  stripeSubscriptionId,
  seatLimits,
}) => {
  const normalizedPlan = String(planType || "").toLowerCase();
  if (normalizedPlan === "paid") return true;
  if (normalizedPlan === "trial" || normalizedPlan === "free") return false;
  if (typeof stripeSubscriptionId === "string" && stripeSubscriptionId.trim()) return true;
  if (hasPaidSeatCapacity(seatLimits)) return true;
  return subscriptionActive === true;
};

export const getFeatureAccessState = ({
  featureKey,
  featureFlags = {},
  userEmail = "",
  planType = null,
  subscriptionActive = false,
  stripeSubscriptionId = "",
  seatLimits = null,
}) => {
  const hiddenEntirelyMap = getFeatureFlagMap(featureFlags, "hiddenEntirelyFeatures");
  const hiddenMap = getFeatureFlagMap(featureFlags, "hiddenFeatures");
  const paidOnlyMap = getFeatureFlagMap(featureFlags, "paidOnlyFeatures");
  const allowMap = getFeatureFlagMap(featureFlags, "featureAllowEmails");
  const lockMessageMap = getFeatureFlagMap(featureFlags, "featureLockMessages");

  const normalizedEmail = normalizeEmail(userEmail);
  const allowEmails = ensureArray(allowMap?.[featureKey]).map(normalizeEmail).filter(Boolean);
  const allowlisted = !!normalizedEmail && allowEmails.includes(normalizedEmail);
  const hiddenEntirely = !!hiddenEntirelyMap?.[featureKey];
  const hidden = !!hiddenMap?.[featureKey];
  const paidOnly = !!paidOnlyMap?.[featureKey];
  const paid = isPaidCompanyState({
    planType,
    subscriptionActive,
    stripeSubscriptionId,
    seatLimits,
  });
  const customLockMessage = (lockMessageMap?.[featureKey] || "").toString().trim();

  if (hiddenEntirely) {
    return {
      allowed: false,
      locked: false,
      hidden: true,
      hiddenEntirely: true,
      paidOnly,
      reason: "hidden_entirely",
      lockMessage: customLockMessage || null,
    };
  }

  if (allowlisted) {
    return {
      allowed: true,
      locked: false,
      hidden,
      hiddenEntirely: false,
      paidOnly,
      reason: "allowlisted",
      lockMessage: customLockMessage || null,
    };
  }

  if (hidden) {
    return {
      allowed: false,
      locked: true,
      hidden: true,
      hiddenEntirely: false,
      paidOnly,
      reason: "hidden",
      lockMessage: customLockMessage || "Hidden by developer",
    };
  }

  if (paidOnly && !paid) {
    return {
      allowed: false,
      locked: true,
      hidden: false,
      hiddenEntirely: false,
      paidOnly: true,
      reason: "paid_only",
      lockMessage: customLockMessage || "Paid plan required",
    };
  }

  return {
    allowed: true,
    locked: false,
    hidden: false,
    hiddenEntirely: false,
    paidOnly,
    reason: null,
    lockMessage: customLockMessage || null,
  };
};

export const getFeatureAccessMap = ({
  featureFlags = {},
  userEmail = "",
  planType = null,
  subscriptionActive = false,
  stripeSubscriptionId = "",
  seatLimits = null,
}) => {
  return FEATURE_KEYS.reduce((acc, featureKey) => {
    acc[featureKey] = getFeatureAccessState({
      featureKey,
      featureFlags,
      userEmail,
      planType,
      subscriptionActive,
      stripeSubscriptionId,
      seatLimits,
    });
    return acc;
  }, {});
};

export const getMaintenanceBannerConfig = (featureFlags = {}) => ({
  enabled: !!featureFlags?.maintenanceBannerEnabled,
  message: (featureFlags?.maintenanceBannerMessage || "").toString(),
  color: (featureFlags?.maintenanceBannerColor || "sky").toString(),
});
